/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { getSessionStorage, getStorage } from "app/client/lib/storage";
import { safeJsonParse } from "app/common/gutil";

import { Observable } from "grainjs";

function getStorageBoolObs(store: Storage, key: string, defValue: boolean) {
  const storedNegation = defValue ? "false" : "true";
  const obs = Observable.create(null, store.getItem(key) === storedNegation ? !defValue : defValue);
  obs.addListener(val => val === defValue ? store.removeItem(key) : store.setItem(key, storedNegation));
  return obs;
}

/**
 * Helper to create a boolean observable whose state is stored in localStorage.
 *
 * Optionally, a default value of true will make the observable start off as true. Note that the
 * same default value should be used for an observable every time it's created.
 */
export function localStorageBoolObs(key: string, defValue = false): Observable<boolean> {
  return getStorageBoolObs(getStorage(), key, defValue);
}

/**
 * Similar to `localStorageBoolObs`, but always uses sessionStorage (or an in-memory equivalent).
 */
export function sessionStorageBoolObs(key: string, defValue = false): Observable<boolean> {
  return getStorageBoolObs(getSessionStorage(), key, defValue);
}

function getStorageObs(store: Storage, key: string, defaultValue?: string) {
  const obs = Observable.create<string | null>(null, store.getItem(key) ?? defaultValue ?? null);
  obs.addListener(val => (val === null) ? store.removeItem(key) : store.setItem(key, val));
  return obs;
}

/**
 * Helper to create a string observable whose state is stored in localStorage.
 */
export function localStorageObs(key: string, defaultValue?: string): Observable<string | null> {
  return getStorageObs(getStorage(), key, defaultValue);
}

/**
 * Similar to `localStorageObs`, but always uses sessionStorage (or an in-memory equivalent).
 */
export function sessionStorageObs(key: string, defaultValue?: string): Observable<string | null> {
  return getStorageObs(getSessionStorage(), key, defaultValue);
}

function getStorageJsonObs<T>(store: Storage, key: string, defaultValue: T): Observable<T> {
  const currentValue = safeJsonParse(store.getItem(key) || "", defaultValue ?? null);
  const obs = Observable.create<T>(null, currentValue);
  obs.addListener(val => (val === null) ? store.removeItem(key) : store.setItem(key, JSON.stringify(val ?? null)));
  return obs;
}

/**
 * Helper to create a JSON observable whose state is stored in localStorage.
 */
export function localStorageJsonObs<T>(key: string, defaultValue: T): Observable<T> {
  return getStorageJsonObs(getStorage(), key, defaultValue);
}

/**
 * Similar to `localStorageJsonObs`, but always uses sessionStorage (or an in-memory equivalent).
 */
export function sessionStorageJsonObs<T>(key: string, defaultValue: T): Observable<T> {
  return getStorageJsonObs(getSessionStorage(), key, defaultValue);
}
